import React, {
  useState,
  useCallback,
  useMemo,
  InputHTMLAttributes,
} from "react";
import debounce from "lodash/debounce";
import classNames from "classnames";

export const defaultDebounceInputClassName =
  "w-full p-2 text-slate-700 border border-slate-200 bg-white rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 hover:border-primary";

export type Props = InputHTMLAttributes<HTMLInputElement> & {
  value?: string;
  onChange?: (val: string) => void;
  debounceTimeout?: number;
  error?: boolean;
  onPressEnterKey?: () => void;
};

function DebounceInput({
  value,
  onChange,
  debounceTimeout = 300,
  type = "text",
  className,
  error,
  onPressEnterKey,
  ...rest
}: Props) {
  const [localValue, setLocalValue] = useState<string | undefined>(value);

  const setDebouncedChange = useMemo(
    () =>
      onChange
        ? debounce((val: string) => {
            onChange(val);
            setLocalValue(undefined);
          }, debounceTimeout)
        : undefined,
    [onChange, debounceTimeout]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setLocalValue(e.target.value);
      setDebouncedChange?.(e.target.value);
    },
    [setDebouncedChange]
  );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      onPressEnterKey?.();
    }
  };

  return (
    <input
      className={classNames(error ? "error-input" : "text-input", className)}
      type={type}
      value={localValue ?? value ?? ""}
      onChange={handleInputChange}
      onKeyDown={handleKeyDown}
      autoComplete="new-password"
      {...rest} // 👉 min, max, step, inputMode, etc.
    />
  );
}

export default DebounceInput;
